import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { useSubscription } from '@apollo/client';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  AmpTransformersInternal,
  MeterPurposeInternal,
  OnPartialConfigChangedSubscription,
  OnPartialConfigChangedSubscriptionVariables,
} from '../../../../../data-access/gql-types/graphql';
import { ON_PARTIAL_CONFIG_CHANGED } from '../../../../../data-access/subscriptions/lavva-devices';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { useTimeout } from '../../../../../hooks/timeout/use-timeout';
import { toastSuccess } from '../../../../../utils/toast';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { ChannelCoverType, ChannelMeterType } from '../../../types/channel';
import { getNumericalValue } from '../../../utils/channels/helpers/state';
import { PartialConfigChangedParams } from '../types';

export const usePartialConfigChanged = ({ channelId, onTariffSuccess }: PartialConfigChangedParams) => {
  const { t } = useTranslation('channel-settings');
  const { updateChannelDetails } = useDevicesAndChannelsContext();
  const { setTimeoutError, clearTimeoutError } = useTimeout();
  const { turnOffBackdrop } = useBackdropContext();
  const { refetchChannelDetails } = useRefetchData();
  const { skipLavvaFetch } = useInstallationContext();
  const { data: configurationData } = useSubscription<
    OnPartialConfigChangedSubscription,
    OnPartialConfigChangedSubscriptionVariables
  >(ON_PARTIAL_CONFIG_CHANGED, {
    variables: { channelId },
    skip: skipLavvaFetch || !channelId,
  });

  useEffect(() => {
    if (configurationData?.onPartialConfigChanged) {
      clearTimeoutError();
      const { numericalProperties, updatedProperties, channelId } = configurationData.onPartialConfigChanged;

      const ampTransformers = getNumericalValue(numericalProperties, 'AmpTransformers');
      const meterPurpose = getNumericalValue(numericalProperties, 'MeterPurpose');
      const sensitivityAOK = getNumericalValue(numericalProperties, 'SensitivityAOK');
      const tariff = updatedProperties.find((x) => x === 'Tariff');

      updateChannelDetails((prev) => {
        if (prev?.id !== channelId) return prev;

        const tempChannel = channelId === prev?.id ? cloneDeep(prev) : undefined;
        if (!tempChannel) return;

        if (tempChannel && ampTransformers?.value) {
          (tempChannel.data as ChannelMeterType).ampTransformers =
            AmpTransformersInternal[ampTransformers.value as keyof typeof AmpTransformersInternal];
        }
        if (tempChannel && meterPurpose?.value) {
          (tempChannel.data as ChannelMeterType).meterPurpose =
            MeterPurposeInternal[meterPurpose.value as keyof typeof MeterPurposeInternal];
        }
        if (tempChannel && sensitivityAOK?.value) {
          (tempChannel.data as ChannelCoverType).sensitivityAOK = parseInt(sensitivityAOK.value);
        }

        return tempChannel;
      });

      if (tariff) {
        refetchChannelDetails();
        onTariffSuccess?.();
      }

      if (numericalProperties.length || updatedProperties.length) {
        toastSuccess({ content: t('toast.editConfigurationSuccess') });
      }

      turnOffBackdrop();
    }
  }, [configurationData]);

  return {
    setTimeoutError,
  };
};
